import { useChat } from "@ai-sdk/react";
import { Input } from "./components/ui/input";
import { MessageAvatar} from "./components/ui/message";
import { Markdown } from "./components/ui/markdown";
import {ArrowUp} from 'lucide-react'
import { useEffect, useRef } from "react";


function ChatWidget() {
  // Get config from URL parameters instead of parent window
  const params = new URLSearchParams(window.location.search);
  const chatbotId = params.get("chatbotId");
  const name = params.get("name");
  const primaryColor = params.get("primaryColor") || "#000000";

   interface TimeSlot {
    start: string;
    end: string;
    readableTime: {
      date: string;
      startTime: string;
      endTime: string;
    };
    duration: number;
  }

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  
  
  if (!chatbotId) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-red-500">
        Chat widget configuration is missing
      </div>
    );
  }

  const { messages, input, handleInputChange, handleSubmit} = useChat({
    api: `https://api.rezigco.com/api/chat/${chatbotId}`,
    credentials: 'include',
    sendExtraMessageFields: true,
    initialMessages: [
      {
        id: "1",
        content: "Hello, how can I help you today?",
        role: "assistant",
      },
    ],
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 p-4 overflow-y-auto space-y-4 min-h-[400px]">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {message.role === "assistant" && (
              <MessageAvatar
                src=""
                className=""
                alt={name || "Ziggy"}
                fallback="ZAI"
              />
            )}
            <div
              className={`max-w-[85%] rounded-[20px] ${
                message.role === "user"
                  ? "bg-gray-100 text-sm px-4 py-2"
                  : "text-sm px-4 py-2"
              }`}
            >
              <div className="font-medium mb-1">
                {message.role === "user" ? "" : name || "Ziggy"}
              </div>
              <div className={message.role === "user" ? "" : ""}>
                {message.parts.map((part) => {
                  if (part.type === "text") {
                    return <Markdown>{part.text}</Markdown>;
                  }
                  if (part.type === "tool-invocation") {
                    const toolInvocation = part.toolInvocation;
                    switch (toolInvocation.toolName) {
                      case "queryAgentContent":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Finding releavant information....</div>;
                          case "call":
                            return <div>Finding relevant information...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;

                      case "summarizeProperty":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Summarizing property...</div>;
                          case "call":
                            return <div>Summarizing property...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;

                      case "getAreaSummaryFromLocation":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return (
                              <div>Getting summary of the location...</div>
                            );
                          case "call":
                            return <div>Getting summary of location...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;

                      case "searchPropertyByIntent":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Searching for properties...</div>;
                          case "call":
                            return <div>Searching for properties...</div>;
                          case "result":
                            return (
                              <div className="space-y-4">
                                {toolInvocation.result.results.map(
                                  (property: any) => (
                                    <div
                                      key={property.id}
                                      className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow"
                                    >
                                      {property.imageUrl && (
                                        <img
                                          src={
                                            property.imageUrl ||
                                            `https://dummyimage.com/600x400/000/fff&text=${property.fullAddress}`
                                          }
                                          onError={(e) => {
                                            e.currentTarget.src = `https://dummyimage.com/600x400/000/fff&text=${property.fullAddress}`;
                                          }}
                                          alt={property.fullAddress}
                                          className="w-full h-32 object-cover rounded-lg mb-3"
                                        />
                                      )}
                                      <div className="space-y-2">
                                        <div className="font-semibold text-lg text-gray-900">
                                          {property.fullAddress}
                                        </div>
                                        <div className="text-primary font-medium text-xl">
                                          {property.price}
                                        </div>
                                        {property.description && (
                                          <div className="text-gray-600 text-sm">
                                            {property.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            );
                        }
                        break;

                      case "findFreeTimeSlots":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Finding free time slots...</div>;
                          case "call":
                            return <div>Finding free time slots...</div>;
                          case "result":
                            return (
                              <div className="space-y-3">
                                {(toolInvocation.result as TimeSlot[]).map(
                                  (slot, index) => (
                                    <div
                                      key={index}
                                      className="border rounded-lg p-4 bg-white cursor-pointer"
                                    >
                                      <div className="font-semibold text-gray-900">
                                        {slot.readableTime.date}
                                      </div>
                                      <div className="text-sm text-gray-600">
                                        {slot.readableTime.startTime} -{" "}
                                        {slot.readableTime.endTime}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        Duration: {slot.duration} minutes
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            );
                        }
                        break;

                      case "getPropertyDetails":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Getting property details...</div>;
                          case "call":
                            return <div>Getting property details...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;

                      case "bookTimeSlot":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Booking time slot...</div>;
                          case "call":
                            return <div>Booking time slot...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;

                      case "findAlternativeTimeSlots":
                        switch (toolInvocation.state) {
                          case "partial-call":
                            return <div>Finding alternative time slots...</div>;
                          case "call":
                            return <div>Finding alternative time slots...</div>;
                          case "result":
                            return (
                              <Markdown>
                                {typeof toolInvocation.result === "string"
                                  ? toolInvocation.result
                                  : JSON.stringify(toolInvocation.result, null, 2)}
                              </Markdown>
                            );
                        }
                        break;
                        
                    }
                  }
                })}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-4 border-t">
        <form onSubmit={handleSubmit} className="relative">
          <Input
            placeholder="Ask anything..."
            name="prompt"
            value={input}
            onChange={handleInputChange}
            className="w-full pr-12 rounded-full resize-none overflow-hidden min-h-[44px] py-2.5"
          />
          <button
            type="submit"
            style={{ backgroundColor: primaryColor }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-full hover:opacity-90 transition-opacity"
          >
            <ArrowUp className="h-4 w-4 text-white" />
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatWidget;
